import { useMemo } from 'react';

import { Forecast } from '@futrli-sdk/predict';
import { ConsolidatedForecastDetail } from '@futrli-sdk/consolidations';

import { ConsolidatedForecastReport, VIEW_TYPES } from '../../../../../../core/types';
import { convertForecastLayoutToGridRows } from './utils';
import { ForecastTree, UKForecastLayoutItem } from '../../../types';
import { filterOutCollapsedTreeNodes, recalculateForecastTree } from '../../../utils/forecast-tree';
import {
  getAccountRowsWithFYInfo,
  getBalanceSheetAccountRowsWithFYInfo
} from '../../../../reports/data-grid/format-rows';
import { REPORT_SECTION } from '../../../../../../core/constants';
import { TotalRow } from '@futrli/predict/forecast/detail/reports/data-grid/types';
import { useForecastColumns } from '../../useForecastColumns/useForecastColumns';
import { usePredictionsInfoContext } from '../../../../context/predictions-info-context';
import { usePredictManagementContext } from '@futrli/predict/predict-management/context/predict-management-context';
import { useProcessedForecastTrees } from '../../useProcessedForecastTrees/useProcessedForecastTrees';
import { useUserGridSettingsContext } from '@futrli/predict/forecast/detail/reports/user-grid-settings/user-grid-settings-context';

export const useUKForecastGrid = (
  forecast: Forecast | ConsolidatedForecastDetail | null | undefined,
  forecastReport: ConsolidatedForecastReport | null | undefined,
  accountNameMap: Record<string, string> = {},
  layoutFunc: (trees: Record<string, ForecastTree>) => UKForecastLayoutItem[],
  addFYInfo: 'none' | 'standard' | 'balanceSheet' = 'none'
) => {
  const { financialConfig } = usePredictManagementContext();
  const { columns, columnsWithFYInfo } = useForecastColumns(forecastReport);
  const { periodType, viewType, thisNumberExplained } = usePredictionsInfoContext();
  const { expandedRows } = useUserGridSettingsContext();

  const section = viewType === VIEW_TYPES.CASH_FLOW ? REPORT_SECTION.CashFlow : REPORT_SECTION.ProfitLoss;

  // We get a few different trees from the backend, balance_sheet Profit_and_loss etc.
  // This hooks combines them and does a bit of processing (removes unused accounts), but we'll do a little more afterwards
  const filteredTrees = useProcessedForecastTrees(forecast, forecastReport, accountNameMap, thisNumberExplained);

  // Get the forecast layout by passing the filtered tree data to the layout generator function
  const layout = useMemo(() => {
    const layout = layoutFunc(filteredTrees);

    // Recalculate all values in tree nodes of each layout item
    layout.forEach(layoutItem => {
      if (layoutItem.nodes) {
        layoutItem.nodes = recalculateForecastTree(layoutItem.nodes);
      }
    });

    // Filter out collapsed nodes from each layout item
    layout.forEach(layoutItem => {
      if (layoutItem.nodes) {
        layoutItem.nodes = filterOutCollapsedTreeNodes(layoutItem.nodes, expandedRows);
      }
    });

    // Apply any last minute before-render tree adjustments
    layout.forEach(layoutItem => {
      if (layoutItem.nodes && layoutItem.adjustTreeBeforeRender) {
        layoutItem.nodes = layoutItem.adjustTreeBeforeRender(layoutItem.nodes);
      }
    });

    return layout;
  }, [expandedRows, filteredTrees, layoutFunc]);

  // Convert the layout data to a flat array of grid rows
  const { totalRow, rows: dataGridRows } = useMemo(() => {
    const data = convertForecastLayoutToGridRows(layout, section);
    let { rows, totalRow } = data;

    // Add the FY totals to the rows if applicable
    if (addFYInfo !== 'none' && financialConfig && periodType === 'MONTHLY' && rows) {
      if (addFYInfo === 'standard') {
        rows = getAccountRowsWithFYInfo(rows, columnsWithFYInfo);
        if (totalRow) {
          totalRow = getAccountRowsWithFYInfo([totalRow], columnsWithFYInfo)[0] as TotalRow;
        }
      } else {
        rows = getBalanceSheetAccountRowsWithFYInfo(rows, columnsWithFYInfo);
        if (totalRow) {
          totalRow = getBalanceSheetAccountRowsWithFYInfo([totalRow], columnsWithFYInfo)[0] as TotalRow;
        }
      }
    }

    return { totalRow, rows };
  }, [addFYInfo, columnsWithFYInfo, financialConfig, layout, periodType, section]);

  return {
    cashPredictedRow: null,
    columns: addFYInfo !== 'none' ? columnsWithFYInfo : columns,
    dataGridRows,
    totalRow
  };
};
